import React from "react";
import ProductTagsTypes from "./ProductTagsTypes";
import { Form } from "react-bootstrap";
import { ProductTag } from "../actions/enums";
import { Product, Type } from "../actions/types";

interface SimilarProductsRowProps {
  loading?: boolean;
  similarProducts: any[];
  originalProductId: number;
  originalProductName: string;
  originalProductSize: string;
  originalQuantity: number;
  saleDetailId: string;
  setSwapProduct: (args: {
    saleDetailId: string;
    productId: string;
    quantity: number;
    originalQuantity: number;
    newProductDescription: string;
    originalProductDescription: string;
  }) => void;
}

const SimilarProductsRow: React.FC<SimilarProductsRowProps> = ({
  loading,
  similarProducts,
  originalProductId,
  originalProductName,
  originalProductSize,
  originalQuantity,
  setSwapProduct,
  saleDetailId,
}) => {
  // Estado para trackear el producto seleccionado y su cantidad
  const [selectedProductId, setSelectedProductId] = React.useState<
    string | null
  >(null);
  const [selectedQuantity, setSelectedQuantity] = React.useState<number>(0);

  const handleSetQuantity =
    ({
      saleDetailId,
      productId,
      originalQuantity,
      newProductDescription,
      originalProductDescription,
    }: {
      saleDetailId: string;
      productId: string;
      originalQuantity: number;
      newProductDescription: string;
      originalProductDescription: string;
    }) =>
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const quantity = parseInt(e.target.value, 10);
      setSelectedProductId(productId);
      setSelectedQuantity(quantity);
      setSwapProduct({
        saleDetailId,
        productId,
        quantity,
        originalQuantity,
        newProductDescription,
        originalProductDescription,
      });
    };

  // Memoized filtered similar products
  const filteredSimilarProducts = React.useMemo(() => {
    if (!Array.isArray(similarProducts)) return [];
    return similarProducts.filter(
      (item: Product) => item.id !== originalProductId,
    );
  }, [similarProducts]);

  if (loading) {
    return (
      <tr className="border-0 border-top-0">
        <td colSpan={4} className="text-center border-0 border-top-0">
          <div className="d-flex align-items-center gap-3">
            {[...Array(5)].map((_, idx) => (
              <span key={idx} className="placeholder-glow flex-fill">
                <span
                  className="placeholder col-10 rounded-2"
                  style={{
                    height: 250,
                    width: 200,
                    display: "inline-block",
                  }}
                ></span>
              </span>
            ))}
          </div>
        </td>
      </tr>
    );
  }
  if (filteredSimilarProducts.length > 0) {
    return (
      <tr>
        <td colSpan={4} style={{ padding: 0, border: 0 }}>
          <div
            style={{
              background: "#f6fafd",
              padding: "1rem",
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              boxShadow: "none",
            }}
          >
            <div
              className="similar-products-grid"
              style={{
                display: "grid",
                justifyItems: "center",
                alignItems: "center",
                gap: 24,
                width: "100%",
                maxWidth: "1200px",
                margin: "0 auto",
              }}
            >
              {filteredSimilarProducts.map((item: any) => (
                <div
                  key={item.id}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    background: "#fff",
                    borderRadius: 10,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    padding: "16px 12px",
                    gap: 12,
                    width: "95%",
                    minHeight: 180,
                  }}
                >
                  <div
                    style={{
                      minWidth: 60,
                      display: "flex",
                      justifyContent: "center",
                      marginBottom: 8,
                    }}
                  >
                    {item.path && (
                      <img
                        alt={item.name}
                        className="rounded"
                        src={item.path}
                        height="100"
                        style={{
                          objectFit: "cover",
                          maxWidth: 100,
                          width: 100,
                          height: 100,
                          borderRadius: 8,
                        }}
                      />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, textAlign: "center" }}>
                    <div
                      style={{
                        fontWeight: 500,
                        fontSize: 15,
                        wordBreak: "break-word",
                        marginBottom: 6,
                      }}
                    >
                      {item.name}
                    </div>
                    {item.typesTalle?.length > 0 && (
                      <ProductTagsTypes
                        types={item.typesTalle}
                        excludeTypes={[ProductTag.WEB]}
                      />
                    )}
                  </div>
                  <div
                    style={{ minWidth: 90, textAlign: "center", marginTop: 8 }}
                  >
                    <Form.Select
                      name="quantity"
                      id={`quantity-${item.id}`}
                      style={{ minWidth: 70, maxWidth: 90 }}
                      value={
                        selectedProductId === item.id ? selectedQuantity : 0
                      }
                      onChange={handleSetQuantity({
                        saleDetailId,
                        productId: item.id,
                        originalQuantity,
                        newProductDescription: `${item.name} - ${
                          item.typesTalle?.map((t: Type) => t.name).join(" · ") || ""
                        }`,
                        originalProductDescription: `${originalProductName} - ${originalProductSize}`,
                      })}
                    >
                      <option key={0} value={0}></option>
                      {Array.from(
                        {
                          length: Math.max(
                            0,
                            Math.min(item?.stock, originalQuantity),
                          ),
                        },
                        (_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {i + 1}
                          </option>
                        ),
                      )}
                    </Form.Select>
                  </div>
                </div>
              ))}
            </div>
            <style>{`
              .similar-products-grid {
                grid-template-columns: repeat(5, 1fr);
                width: 100%;
              }
              @media (max-width: 1200px) {
                .similar-products-grid {
                  grid-template-columns: repeat(3, 1fr) !important;
                }
              }
              @media (max-width: 700px) {
                .similar-products-grid {
                  grid-template-columns: 1fr !important;
                }
              }
            `}</style>
          </div>
        </td>
      </tr>
    );
  }
  return (
    <tr className="border-0 border-top-0">
      <td colSpan={4} className="text-center border-0 border-top-0">
        <span className="text-muted">No hay productos similares en stock.</span>
      </td>
    </tr>
  );
};

export default SimilarProductsRow;
